import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDAutocomplete from "components/MDAutocomplete";
import api from "services/api";
import { resolveUploadUrl } from "utils";

const defaultForm = {
  caption: "",
  status: "AKTIF",
  urutan: "0",
};

const STATUS_OPTIONS = ["AKTIF", "NONAKTIF"];

function GaleriFormDialog({ open, mode, item, onClose, onSaved }) {
  const [form, setForm] = useState(defaultForm);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && item) {
      setForm({
        caption: item.caption || "",
        status: item.status || "AKTIF",
        urutan: String(item.urutan ?? 0),
      });
      setPreview(item.file_url ? resolveUploadUrl(item.file_url) : "");
    } else {
      setForm(defaultForm);
      setPreview("");
    }
    setFile(null);
  }, [open, mode, item]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSelectChange = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    setFile(selected || null);
    if (selected) {
      setPreview(URL.createObjectURL(selected));
    } else if (mode === "edit" && item?.file_url) {
      setPreview(resolveUploadUrl(item.file_url));
    } else {
      setPreview("");
    }
  };

  const handleSubmit = async () => {
    if (!form.caption.trim()) {
      alert("Caption wajib diisi");
      return;
    }
    if (mode === "create" && !file) {
      alert("Foto wajib diupload");
      return;
    }

    const payload = new FormData();
    payload.append("caption", form.caption.trim());
    payload.append("status", form.status);
    payload.append("urutan", form.urutan);
    if (file) payload.append("file", file);

    setSaving(true);
    try {
      if (mode === "edit" && item?.id) {
        await api.put(`galeri-aktivitas/${item.id}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.post("galeri-aktivitas", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      onSaved();
      onClose();
    } catch (error) {
      alert(error.response?.data?.message || "Gagal menyimpan foto aktivitas");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{mode === "edit" ? "Edit Foto Aktivitas" : "Tambah Foto Aktivitas"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12}>
            <MDInput
              label="Caption *"
              fullWidth
              multiline
              rows={3}
              value={form.caption}
              onChange={handleChange("caption")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDAutocomplete
              label="Status"
              fullWidth
              options={STATUS_OPTIONS}
              value={form.status}
              onChange={handleSelectChange("status")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDInput
              type="number"
              label="Urutan"
              fullWidth
              value={form.urutan}
              onChange={handleChange("urutan")}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDTypography variant="caption" color="text" display="block" mb={1}>
              Foto {mode === "create" ? "*" : "(kosongkan jika tidak diganti)"}
            </MDTypography>
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFileChange} />
          </Grid>
          {preview ? (
            <Grid item xs={12}>
              <MDBox
                component="img"
                src={preview}
                alt="Preview"
                borderRadius="lg"
                width="100%"
                maxHeight="240px"
                sx={{ objectFit: "cover" }}
              />
            </Grid>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton variant="text" color="secondary" onClick={onClose}>
          Batal
        </MDButton>
        <MDButton variant="gradient" color="info" onClick={handleSubmit} disabled={saving}>
          {saving ? "Menyimpan..." : "Simpan"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

GaleriFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["create", "edit"]).isRequired,
  item: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default GaleriFormDialog;
